import { dealersApi } from '../features/dealer';
import { geographicApi } from '../features/geographic';
import { locationsApi } from '../features/location';
import { manufacturersApi } from '../features/manufacturer';
import { ordersApi } from '../features/order';
import { systemSettingsApi } from '../features/settings/system-settings-api-slice';
import { techniciansApi } from '../features/technician';
import { usersApi, UserService } from '../features/user';
import { logout as logoutAction, setCurrentUser } from '../features/user/user-slice';
import { router } from '../routes';
import { store } from '../store';
import { User, UserRole } from '../types';
import { logError, logInfo } from '../utils';
import keycloakService from './keycloak-service';

/**
 * Service for handling authentication
 */
export class AuthService {
  private readonly PREVIOUS_USER_ID_KEY = 'mytruss_previous_user_id';
  private readonly REDIRECT_TO_HOME_KEY = 'mytruss_redirect_to_home';

  /**
   * Check if a path is an order-specific route that should redirect to home on user change
   * @param path The pathname to check
   * @returns True if the path is an order-specific route
   */
  private isOrderSpecificRoute(path: string): boolean {
    return path.startsWith('/order-details/') || /\/manage-buildings(\/|$)/.test(path);
  }

  /**
   * Get the previous user ID from sessionStorage
   * @returns The previous user ID or null if not found
   */
  private getPreviousUserId(): string | null {
    try {
      return sessionStorage.getItem(this.PREVIOUS_USER_ID_KEY);
    } catch (error) {
      logError('Failed to access sessionStorage for previous user ID:', error);
      return null;
    }
  }

  /**
   * Set the previous user ID in sessionStorage
   * @param userId The user ID to store, or null to remove
   */
  private setPreviousUserId(userId: string | null): void {
    try {
      if (userId === null) {
        sessionStorage.removeItem(this.PREVIOUS_USER_ID_KEY);
      } else {
        sessionStorage.setItem(this.PREVIOUS_USER_ID_KEY, userId);
      }
    } catch (error) {
      logError('Failed to update sessionStorage for previous user ID:', error);
    }
  }

  /**
   * Get the redirect to home flag from sessionStorage
   * @returns True if the flag is set
   */
  private getRedirectToHomeFlag(): boolean {
    try {
      return sessionStorage.getItem(this.REDIRECT_TO_HOME_KEY) === 'true';
    } catch (error) {
      logError('Failed to access sessionStorage for redirect flag:', error);
      return false;
    }
  }

  /**
   * Set the redirect to home flag in sessionStorage
   * @param value True to set the flag, false to remove it
   */
  private setRedirectToHomeFlag(value: boolean): void {
    try {
      if (value) {
        sessionStorage.setItem(this.REDIRECT_TO_HOME_KEY, 'true');
      } else {
        sessionStorage.removeItem(this.REDIRECT_TO_HOME_KEY);
      }
    } catch (error) {
      logError('Failed to update sessionStorage for redirect flag:', error);
    }
  }

  /**
   * Initialize the authentication service
   * @returns Promise resolving to true if initialization was successful
   */
  async init(): Promise<boolean> {
    try {
      // Check immediately if we're on an order-specific route and might need to redirect
      // This happens before Keycloak initialization to catch redirects early
      const currentPath = window.location.pathname;
      const isOrderSpecificRoute = this.isOrderSpecificRoute(currentPath);
      const shouldRedirectToHome = this.getRedirectToHomeFlag();

      // If we have a redirect flag set and we're on an order-specific route, redirect immediately
      if (shouldRedirectToHome && isOrderSpecificRoute) {
        this.setRedirectToHomeFlag(false);
        router.navigate('/', { replace: true });
        // Continue with initialization after navigation (React Router doesn't reload the page)
      }

      // Initialize Keycloak
      const initialized = await keycloakService.init();

      if (initialized) {
        // Register event handlers
        this.registerEventHandlers();

        // If authenticated, load the user profile and check for user change
        if (keycloakService.isAuthenticated()) {
          await this.loadUserProfile();

          // Check if user has changed after profile load
          const currentUser = store.getState().user.currentUser;
          if (currentUser) {
            const currentUserId = currentUser.id;
            const storedPreviousUserId = this.getPreviousUserId();
            const currentPath = window.location.pathname;
            const isHomePage = currentPath === '/' || currentPath === '';

            // If there's no previous user ID stored, this is a new login session
            // Always redirect to home for new logins to ensure users start at base URL
            if (storedPreviousUserId === null && !isHomePage) {
              logInfo(`New login session detected (${currentUserId}), redirecting to home`);
              this.setRedirectToHomeFlag(false);
              router.navigate('/', { replace: true });
              this.setPreviousUserId(currentUserId);
              return initialized;
            }

            // If this is a different user login (user switched accounts), redirect to home
            if (storedPreviousUserId !== null && storedPreviousUserId !== currentUserId) {
              logInfo(`New user login detected (${currentUserId}), redirecting to home`);
              this.setRedirectToHomeFlag(false);
              router.navigate('/', { replace: true });
              this.setPreviousUserId(currentUserId);
              return initialized;
            }

            // Also check if we should redirect based on the redirect flag
            const redirectFlag = this.getRedirectToHomeFlag();
            if (redirectFlag && isOrderSpecificRoute) {
              this.setRedirectToHomeFlag(false);
              logInfo(`Redirect flag detected on order-specific route, redirecting to home`);
              router.navigate('/', { replace: true });
              this.setPreviousUserId(currentUserId);
              return initialized;
            }

            // Clean up redirect flag if it exists (in case user navigated away before redirect)
            if (redirectFlag) {
              this.setRedirectToHomeFlag(false);
            }

            // Update stored user ID
            this.setPreviousUserId(currentUserId);
          }
        }
      }

      return initialized;
    } catch (error) {
      logError('Failed to initialize AuthService:', error);
      return false;
    }
  }

  /**
   * Login the user
   * @param redirectUri Optional redirect URI after login
   * @returns Promise resolving when login is complete
   */
  login(redirectUri?: string): Promise<void> {
    return keycloakService.login({
      redirectUri: redirectUri ?? window.location.origin,
    });
  }

  /**
   * Logout the user
   * @returns Promise resolving when logout is complete
   */
  async logout(): Promise<void> {
    try {
      // Dispatch logout action to clear user state
      store.dispatch(logoutAction());

      // Store current path before logout so we can redirect away from it on next login
      const currentPath = window.location.pathname;
      const isOrderSpecificRoute = this.isOrderSpecificRoute(currentPath);

      // Reset all RTK Query API caches to prevent data leakage between users
      store.dispatch(usersApi.util.resetApiState());
      store.dispatch(manufacturersApi.util.resetApiState());
      store.dispatch(dealersApi.util.resetApiState());
      store.dispatch(geographicApi.util.resetApiState());
      store.dispatch(techniciansApi.util.resetApiState());
      store.dispatch(ordersApi.util.resetApiState());
      store.dispatch(locationsApi.util.resetApiState());
      store.dispatch(systemSettingsApi.util.resetApiState());

      // If logging out from an order-specific route, set a flag to redirect on next login
      if (isOrderSpecificRoute) {
        this.setRedirectToHomeFlag(true);
      }

      // Reset previous user ID tracking
      this.setPreviousUserId(null);

      await keycloakService.logout();
    } catch (error) {
      logError('Failed to logout:', error);
      throw error;
    }
  }

  /**
   * Check if the user is authenticated
   * @returns True if the user is authenticated
   */
  isAuthenticated(): boolean {
    return keycloakService.isAuthenticated();
  }

  /**
   * Get the authentication token
   * @returns The authentication token or null if not authenticated
   */
  getToken(): string | null {
    return keycloakService.getToken();
  }

  /**
   * Get the refresh token
   * @returns The refresh token or null if not authenticated
   */
  getRefreshToken(): string | null {
    return keycloakService.getRefreshToken();
  }

  /**
   * Load the user profile from Keycloak
   * @returns Promise resolving when the profile is loaded
   */
  async loadUserProfile(): Promise<void> {
    try {
      if (!keycloakService.isAuthenticated()) {
        return;
      }

      const keycloak = keycloakService.getKeycloak();
      const profile = await keycloak.loadUserProfile();
      const tokenParsed = keycloakService.getTokenParsed();

      if (!profile || !tokenParsed) {
        return;
      }

      // Map Keycloak profile to basic User model
      const user: User = {
        id: tokenParsed.sub ?? 'UNKNOWN',
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        email: profile.email ?? '',
        phone: '',
        role: this.mapKeycloakRolesToUserRole(tokenParsed.realm_access?.roles ?? []),
        active: true,
      };

      // Update Redux store with basic user profile
      store.dispatch(setCurrentUser(user));

      // Get additional user information from the backend
      try {
        const userService = new UserService();
        const extendedUser = await userService.getUserById(user.id);

        const mergedUser: User = {
          ...user,
          phone: extendedUser.phone || '',
          manufacturerId: extendedUser.manufacturerId,
          dealerId: extendedUser.dealerId,
          dealer: extendedUser.dealer,
          manufacturer: extendedUser.manufacturer,
          avatar: extendedUser.avatar,
          allowSignOffOnTasks: extendedUser.allowSignOffOnTasks ?? false,
        };

        // Update Redux store with the complete user profile
        store.dispatch(setCurrentUser(mergedUser));

        logInfo('User profile loaded successfully with extended data');
      } catch (error) {
        // If we can't get the extended profile, we'll just use the basic one
        logError('Failed to load extended user profile:', error);
        // The basic user profile was already dispatched, so the app can still function
      }
    } catch (error) {
      logError('Failed to load user profile:', error);
    }
  }

  /**
   * Map Keycloak roles to UserRole
   * @param roles Array of Keycloak roles
   * @returns The corresponding UserRole
   */
  private mapKeycloakRolesToUserRole(roles: string[]): UserRole {
    if (roles.includes('administrator')) {
      return UserRole.ADMINISTRATOR;
    } else if (roles.includes('supervisor')) {
      return UserRole.SUPERVISOR;
    } else if (roles.includes('csr')) {
      return UserRole.CSR;
    } else if (roles.includes('user')) {
      return UserRole.DEALER;
    }

    return UserRole.DEALER;
  }

  /**
   * Register event handlers for Keycloak events
   */
  private registerEventHandlers(): void {
    // Handle token expiration
    keycloakService.onTokenExpired(() => {
      logInfo('Token expired, refreshing...');
      keycloakService.updateToken(0).catch(() => {
        // If token refresh fails, redirect to login
        this.login();
      });
    });

    // Handle successful authentication
    // Note: This primarily handles login-after-initial-load scenarios.
    // The main logout/login flow is handled in init() which runs on page load.
    keycloakService.onAuthSuccess(() => {
      logInfo('Authentication successful');
      this.loadUserProfile().then(() => {
        const currentUser = store.getState().user.currentUser;
        if (currentUser) {
          const currentUserId = currentUser.id;
          const previousUserId = this.getPreviousUserId();
          const currentPath = window.location.pathname;
          const isHomePage = currentPath === '/' || currentPath === '';

          // If there's no previous user ID stored, this is a new login session
          // Always redirect to home for new logins to ensure users start at base URL
          if (previousUserId === null && !isHomePage) {
            logInfo(`New login session detected (${currentUserId}), redirecting to home`);
            this.setPreviousUserId(currentUserId);
            router.navigate('/', { replace: true });
            return;
          }

          const isNewUserLogin = previousUserId !== null && previousUserId !== currentUserId;

          // Update the stored previous user ID
          this.setPreviousUserId(currentUserId);

          // Redirect if this is a new user login (user switched accounts)
          if (isNewUserLogin) {
            logInfo(`New user login detected (${currentUserId}), redirecting to home`);
            router.navigate('/', { replace: true });
          }
        }
      });
    });

    // Handle authentication error
    keycloakService.onAuthError(() => {
      logError('Authentication error');
      store.dispatch(logoutAction());
    });

    // Handle successful token refresh
    keycloakService.onAuthRefreshSuccess(() => {
      logInfo('Token refresh successful');
    });

    // Handle token refresh error
    keycloakService.onAuthRefreshError(() => {
      logError('Token refresh error');
      // If token refresh fails, redirect to login
      this.login();
    });

    // Handle logout
    keycloakService.onAuthLogout(() => {
      logInfo('Logged out');
      store.dispatch(logoutAction());
    });
  }
}

// Create a singleton instance
const authService = new AuthService();

export default authService;
